import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { useSearchParams, Link } from "react-router-dom";
import { Loader2, UploadCloud, Send, Inbox, Plus, FileBox, Clock, MessageSquare, Network } from "lucide-react";
import { toast } from "sonner";
import FindLab from "@/components/FindLab";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
    new: "bg-cyan-100 text-cyan-800",
    accepted: "bg-blue-100 text-blue-800",
    in_lab: "bg-amber-100 text-amber-800",
    ready: "bg-emerald-100 text-emerald-800",
    delivered: "bg-slate-200 text-slate-700",
    cancelled: "bg-rose-100 text-rose-700",
};

function StatusPill({ status }) {
    return (
        <span className={`px-2.5 py-0.5 rounded-full text-[11.5px] font-medium capitalize ${STATUS_STYLES[status] || "bg-slate-100 text-slate-600"}`}>
            {String(status || "").replace("_", " ")}
        </span>
    );
}

export default function PracticePortal() {
    const [params] = useSearchParams();
    const [token, setToken] = useState(null);
    const [practice, setPractice] = useState(null);
    const [orders, setOrders] = useState([]);
    const [view, setView] = useState("orders"); // orders | new | labs
    const [loading, setLoading] = useState(true);
    const [authFailed, setAuthFailed] = useState(false);
    const [pendingLabs, setPendingLabs] = useState(0);

    const loadPending = useCallback(async (t) => {
        try {
            const r = await axios.get(`${API}/connect/practice/pending-count`, { headers: { "X-Practice-Token": t } });
            setPendingLabs(r.data.count || 0);
        } catch { /* badge is best-effort */ }
    }, []);

    useEffect(() => {
        const qp = params.get("token");
        const stored = localStorage.getItem("practice_token");
        const t = qp || stored;
        if (qp) localStorage.setItem("practice_token", qp);
        setToken(t || "");
    }, [params]);

    const loadOrders = useCallback(async (t) => {
        const res = await axios.get(`${API}/practice/scan-orders`, { headers: { "X-Practice-Token": t } });
        setOrders(res.data);
    }, []);

    useEffect(() => {
        if (token === null) return;
        if (!token) { setLoading(false); setAuthFailed(true); return; }
        (async () => {
            try {
                const me = await axios.get(`${API}/practice/me`, { headers: { "X-Practice-Token": token } });
                setPractice(me.data);
                if (me.data.status === "approved") {
                    await loadOrders(token);
                    loadPending(token);
                }
            } catch {
                setAuthFailed(true);
            } finally {
                setLoading(false);
            }
        })();
    }, [token, loadOrders]);

    if (loading) return <div className="py-32 text-center"><Loader2 className="animate-spin mx-auto text-slate-400" /></div>;

    if (authFailed || !practice) {
        return (
            <div className="max-w-md mx-auto px-6 py-24 text-center" data-testid="practice-portal-noauth">
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light text-slate-900">Portal link required.</h1>
                <p className="mt-4 text-[15px] text-slate-600">Open this page via the personal link the lab emailed you, or register your practice first.</p>
                <Link to="/practice/register" className="inline-block mt-7 px-6 py-3 rounded-full bg-slate-900 text-white text-[14px]">Register practice</Link>
            </div>
        );
    }

    if (practice.status !== "approved") {
        return (
            <div className="max-w-md mx-auto px-6 py-24 text-center" data-testid="practice-portal-pending">
                <Clock size={36} className="text-amber-500 mx-auto mb-4" />
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light text-slate-900">Pending approval.</h1>
                <p className="mt-4 text-[15px] text-slate-600">
                    <strong>{practice.practice_name}</strong> is being reviewed by the lab. You&apos;ll receive an email as soon as your portal is activated.
                </p>
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto px-6 py-12" data-testid="practice-portal">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
                <div>
                    <div className="text-[11px] font-mono-tr tracking-[0.24em] text-cyan-600 uppercase mb-1.5">Practice portal</div>
                    <h1 className="text-2xl font-medium text-slate-900">{practice.practice_name}</h1>
                </div>
                <div className="flex gap-2">
                    <button data-testid="pp-tab-orders" onClick={() => setView("orders")}
                        className={`px-4 py-2 rounded-full text-[13px] font-medium transition-colors ${view === "orders" ? "bg-slate-900 text-white" : "border border-slate-300 text-slate-700 hover:border-slate-500"}`}>
                        <Inbox size={13} className="inline me-1.5 -mt-0.5" />My orders
                    </button>
                    <button data-testid="pp-tab-new" onClick={() => setView("new")}
                        className={`px-4 py-2 rounded-full text-[13px] font-medium transition-colors ${view === "new" ? "bg-slate-900 text-white" : "border border-slate-300 text-slate-700 hover:border-slate-500"}`}>
                        <Plus size={13} className="inline me-1.5 -mt-0.5" />New scan order
                    </button>
                    <button data-testid="pp-tab-labs" onClick={() => setView("labs")}
                        className={`px-4 py-2 rounded-full text-[13px] font-medium transition-colors ${view === "labs" ? "bg-slate-900 text-white" : "border border-slate-300 text-slate-700 hover:border-slate-500"}`}>
                        <Network size={13} className="inline me-1.5 -mt-0.5" />My labs
                        {pendingLabs > 0 && (
                            <span data-testid="pp-labs-badge" className="ms-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10.5px] font-bold leading-none align-middle">{pendingLabs}</span>
                        )}
                    </button>
                </div>
            </div>
            {view === "labs" && <FindLab token={token} onChanged={() => loadPending(token)} />}
            {view === "new" && <NewOrderForm token={token} onCreated={() => { setView("orders"); loadOrders(token); }} />}
            {view === "orders" && (
                <>
                    <TestimonialInvite practice={practice} token={token} />
                    <OrderList orders={orders} token={token} reload={() => loadOrders(token)} />
                    <ReferralCard practice={practice} />
                </>
            )}
        </div>
    );
}

function ReferralCard({ practice }) {
    if (!practice.referral_code) return null;
    const link = `${window.location.origin}/practice/register?ref=${practice.referral_code}`;
    const credits = practice.referral_credits || 0;
    return (
        <div className="mt-8 rounded-2xl border border-slate-200 bg-slate-50 p-5" data-testid="pp-referral-card">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <div className="text-[14px] font-semibold text-slate-900">Refer a colleague practice</div>
                    <p className="text-[12.5px] text-slate-600 mt-0.5">
                        Share your link — when their practice is approved, the referral is credited to you.
                        {credits > 0 && <span className="ms-2 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-[11px] font-semibold" data-testid="pp-referral-credits">{credits} referral{credits > 1 ? "s" : ""} credited</span>}
                    </p>
                </div>
                <button
                    onClick={() => { navigator.clipboard?.writeText(link); toast.success("Referral link copied — share it with a colleague"); }}
                    data-testid="pp-referral-copy"
                    className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-slate-900 hover:bg-slate-700 text-white text-[12.5px] font-medium">
                    Copy referral link
                </button>
            </div>
            <div className="mt-3 px-3.5 py-2.5 rounded-lg bg-white border border-slate-200 text-[12px] text-slate-500 font-mono break-all" data-testid="pp-referral-link">{link}</div>
        </div>
    );
}

function TestimonialInvite({ practice, token }) {
    const [quote, setQuote] = useState("");
    const [author, setAuthor] = useState(practice.contact_name || "");
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);
    if (!practice.testimonial_requested_at || practice.testimonial_submitted_at || done === "hidden") return null;

    const submit = async () => {
        if (quote.trim().length < 10) { toast.error("Please write at least a short sentence"); return; }
        setBusy(true);
        try {
            await axios.post(`${API}/practice/testimonial`, { quote, author }, { headers: { "X-Practice-Token": token } });
            setDone(true);
        } catch (e) { toast.error(e?.response?.data?.detail || "Could not submit — try again"); }
        setBusy(false);
    };

    return (
        <div className="mb-6 rounded-2xl border border-cyan-200 bg-cyan-50/60 p-5" data-testid="pp-testimonial-invite">
            {done ? (
                <div className="flex items-center justify-between gap-3">
                    <p className="text-[13.5px] text-slate-700"><strong>Thank you!</strong> The lab will review your quote before it goes live.</p>
                    <button onClick={() => setDone("hidden")} data-testid="pp-testimonial-dismiss" className="text-[12.5px] text-slate-500 hover:text-slate-800">Close</button>
                </div>
            ) : (
                <>
                    <div className="text-[14px] font-semibold text-slate-900 mb-1">The lab would love a short quote from you</div>
                    <p className="text-[12.5px] text-slate-600 mb-3">One or two sentences about working together — it may appear on the public Toothmatrix Practice page.</p>
                    <textarea value={quote} onChange={(e) => setQuote(e.target.value)} rows={3} maxLength={400} data-testid="pp-testimonial-quote"
                        placeholder="e.g. “Every case is trackable now — no more chasing the lab on WhatsApp.”"
                        className="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 bg-white outline-none focus:border-cyan-500 text-[13px] mb-3" />
                    <div className="flex flex-wrap items-center gap-3">
                        <input value={author} onChange={(e) => setAuthor(e.target.value)} maxLength={80} data-testid="pp-testimonial-author" placeholder="Your name (e.g. Dr. …)"
                            className="flex-1 min-w-[180px] px-3.5 py-2.5 rounded-xl border border-slate-300 bg-white outline-none focus:border-cyan-500 text-[13px]" />
                        <button onClick={submit} disabled={busy} data-testid="pp-testimonial-submit"
                            className="px-5 py-2.5 rounded-xl bg-cyan-600 hover:bg-cyan-500 disabled:bg-slate-400 text-white text-[13px] font-medium">
                            {busy ? "Sending…" : "Send quote"}
                        </button>
                    </div>
                </>
            )}
        </div>
    );
}

function NewOrderForm({ token, onCreated }) {
    const [form, setForm] = useState({ patient_ref: "", doctor: "", item: "", tooth: "", material: "", shade: "", due_date: "", notes: "" });
    const [files, setFiles] = useState([]);
    const [labs, setLabs] = useState([]);
    const [labId, setLabId] = useState("");
    const [uploading, setUploading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const inputRef = useRef(null);
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    useEffect(() => {
        axios.get(`${API}/connect/practice/connections`, { headers: { "X-Practice-Token": token } })
            .then((r) => {
                const c = r.data.connected || [];
                setLabs(c);
                if (c.length) setLabId(c[0].tenant_id);
            })
            .catch(() => {});
    }, [token]);

    const onFiles = async (list) => {
        setUploading(true);
        try {
            for (const file of Array.from(list).slice(0, 10)) {
                const fd = new FormData();
                fd.append("file", file);
                const res = await axios.post(`${API}/files/upload`, fd);
                setFiles((prev) => [...prev, res.data]);
            }
            toast.success("Files uploaded");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Upload failed");
        } finally {
            setUploading(false);
        }
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!form.item.trim()) { toast.error("What should the lab make? Fill the item field."); return; }
        setSubmitting(true);
        try {
            await axios.post(`${API}/practice/scan-orders`, { ...form, lab_tenant_id: labId, file_ids: files.map((f) => f.id) }, { headers: { "X-Practice-Token": token } });
            toast.success("Scan order sent to the lab");
            onCreated();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not create order");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <form onSubmit={submit} className="space-y-4" data-testid="pp-new-order-form">
            {labs.length > 0 && (
                <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                    <label className="text-[13px] font-medium text-slate-700">Send to lab</label>
                    <select data-testid="pp-lab-select" value={labId} onChange={(e) => setLabId(e.target.value)}
                        className="flex-1 min-w-[200px] px-3.5 py-2 rounded-xl border border-slate-300 bg-white outline-none focus:border-cyan-500 text-[13.5px]">
                        {labs.map((l) => <option key={l.tenant_id} value={l.tenant_id}>{l.lab_name}</option>)}
                    </select>
                </div>
            )}
            <div
                onClick={() => inputRef.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); onFiles(e.dataTransfer.files); }}
                data-testid="pp-dropzone"
                className="rounded-2xl border-2 border-dashed border-slate-300 hover:border-cyan-500 transition-colors p-8 text-center cursor-pointer"
            >
                <UploadCloud size={26} className="mx-auto text-slate-400 mb-2" />
                <div className="text-[14px] text-slate-700 font-medium">Drop scan files here or click to browse</div>
                <div className="text-[12px] text-slate-400 mt-1">STL · DICOM (.dcm) · PLY · OBJ · photos — up to 100 MB each</div>
                <input ref={inputRef} type="file" multiple hidden accept=".stl,.ply,.obj,.dcm,.zip,.jpg,.jpeg,.png,.webp" onChange={(e) => onFiles(e.target.files)} />
                {uploading && <Loader2 size={16} className="animate-spin mx-auto mt-3 text-cyan-600" />}
            </div>
            {files.length > 0 && (
                <div className="flex flex-wrap gap-2">
                    {files.map((f) => (
                        <span key={f.id} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-100 text-[12.5px] text-slate-700">
                            <FileBox size={12} /> {f.name} <span className="text-slate-400">{(f.size / 1024 / 1024).toFixed(1)} MB</span>
                        </span>
                    ))}
                </div>
            )}
            <div className="grid sm:grid-cols-2 gap-4">
                <input data-testid="pp-item" value={form.item} onChange={set("item")} placeholder="Item — e.g. Implant crown *" className="px-4 py-3 rounded-xl border border-slate-300 focus:border-cyan-500 outline-none text-[14px]" />
                <input data-testid="pp-tooth" value={form.tooth} onChange={set("tooth")} placeholder="Tooth / FDI — e.g. 36" className="px-4 py-3 rounded-xl border border-slate-300 focus:border-cyan-500 outline-none text-[14px]" />
                <input data-testid="pp-material" value={form.material} onChange={set("material")} placeholder="Material — e.g. Zirconia" className="px-4 py-3 rounded-xl border border-slate-300 focus:border-cyan-500 outline-none text-[14px]" />
                <input data-testid="pp-shade" value={form.shade} onChange={set("shade")} placeholder="Shade — e.g. A2" className="px-4 py-3 rounded-xl border border-slate-300 focus:border-cyan-500 outline-none text-[14px]" />
                <input data-testid="pp-patient" value={form.patient_ref} onChange={set("patient_ref")} placeholder="Patient reference (no full name needed)" className="px-4 py-3 rounded-xl border border-slate-300 focus:border-cyan-500 outline-none text-[14px]" />
                <input data-testid="pp-due" type="date" value={form.due_date} onChange={set("due_date")} className="px-4 py-3 rounded-xl border border-slate-300 focus:border-cyan-500 outline-none text-[14px] text-slate-600" />
            </div>
            <input data-testid="pp-doctor" value={form.doctor} onChange={set("doctor")} placeholder="Treating doctor" className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:border-cyan-500 outline-none text-[14px]" />
            <textarea data-testid="pp-notes" value={form.notes} onChange={set("notes")} placeholder="Notes for the lab — e.g. patient grinds, occlusal clearance…" rows={3} className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:border-cyan-500 outline-none text-[14px]" />
            <button data-testid="pp-submit-order" disabled={submitting || uploading}
                className="w-full py-3.5 rounded-xl bg-cyan-600 hover:bg-cyan-500 disabled:bg-slate-400 text-white text-[14px] font-semibold transition-colors inline-flex items-center justify-center gap-2">
                {submitting ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
                Send to lab
            </button>
        </form>
    );
}

function OrderList({ orders, token, reload }) {
    const [openId, setOpenId] = useState(null);
    const [msg, setMsg] = useState("");
    const [sending, setSending] = useState(false);

    const sendMsg = async (orderId) => {
        if (!msg.trim()) return;
        setSending(true);
        try {
            await axios.post(`${API}/practice/scan-orders/${orderId}/messages`, { text: msg }, { headers: { "X-Practice-Token": token } });
            setMsg("");
            await reload();
        } catch {
            toast.error("Message failed");
        } finally {
            setSending(false);
        }
    };

    if (!orders.length) {
        return <div className="text-center py-16 text-slate-400 text-[14px]" data-testid="pp-empty">No orders yet — send your first scan with &ldquo;New scan order&rdquo;.</div>;
    }
    return (
        <div className="space-y-3" data-testid="pp-order-list">
            {orders.map((o) => (
                <div key={o.id} className="rounded-2xl border border-slate-200 overflow-hidden">
                    <button onClick={() => setOpenId(openId === o.id ? null : o.id)} data-testid={`pp-order-${o.order_no}`}
                        className="w-full flex flex-wrap items-center justify-between gap-3 px-5 py-4 text-left hover:bg-slate-50 transition-colors">
                        <div>
                            <span className="font-mono-tr text-[12px] text-slate-400 me-3">{o.order_no}</span>
                            <span className="text-[14.5px] font-medium text-slate-900">{o.item || "Scan order"}</span>
                            {o.tooth && <span className="text-[13px] text-slate-500 ms-2">· {o.tooth}</span>}
                        </div>
                        <div className="flex items-center gap-3">
                            {o.messages?.length > 0 && <span className="text-[12px] text-slate-400"><MessageSquare size={12} className="inline -mt-0.5 me-1" />{o.messages.length}</span>}
                            <StatusPill status={o.status} />
                        </div>
                    </button>
                    {openId === o.id && (
                        <div className="px-5 pb-5 border-t border-slate-100 pt-4">
                            <div className="grid sm:grid-cols-3 gap-3 text-[13px] text-slate-600 mb-4">
                                <div><span className="text-slate-400">Material:</span> {o.material || "—"}</div>
                                <div><span className="text-slate-400">Shade:</span> {o.shade || "—"}</div>
                                <div><span className="text-slate-400">Due:</span> {o.due_date || "—"}</div>
                                {o.lab_name && <div data-testid={`pp-order-lab-${o.order_no}`}><span className="text-slate-400">Lab:</span> {o.lab_name}</div>}
                            </div>
                            {o.files?.length > 0 && (
                                <div className="flex flex-wrap gap-2 mb-4">
                                    {o.files.map((f, i) => <span key={i} className="px-2.5 py-1 rounded bg-slate-100 text-[12px] text-slate-600"><FileBox size={11} className="inline me-1 -mt-0.5" />{f.name}</span>)}
                                </div>
                            )}
                            <div className="space-y-2 mb-3">
                                {(o.messages || []).map((m) => (
                                    <div key={m.id} className={`max-w-md rounded-xl px-3.5 py-2.5 text-[13px] ${m.from === "practice" ? "bg-cyan-50 text-slate-800 ms-auto" : "bg-slate-100 text-slate-800"}`}>
                                        <div className="text-[11px] text-slate-400 mb-0.5">{m.author} · {new Date(m.at).toLocaleString()}</div>
                                        {m.text}
                                    </div>
                                ))}
                            </div>
                            <div className="flex gap-2">
                                <input value={msg} onChange={(e) => setMsg(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendMsg(o.id)}
                                    data-testid="pp-msg-input" placeholder="Message the lab about this order…"
                                    className="flex-1 px-4 py-2.5 rounded-xl border border-slate-300 focus:border-cyan-500 outline-none text-[13.5px]" />
                                <button onClick={() => sendMsg(o.id)} disabled={sending} data-testid="pp-msg-send"
                                    className="px-4 rounded-xl bg-slate-900 text-white disabled:bg-slate-400"><Send size={14} /></button>
                            </div>
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
}
